/* eslint-disable functional/no-expression-statement */
import * as D3Brush from "d3-brush";
import * as D3Selection from "d3-selection";

import { Timeline } from "~/Timeline";
import { Scrolling, Brush, ZoneProp } from "~/Timeline/Brusher";

export type SelectorBrushSelection = readonly [number, number];

export const defaultSelection = (
  maximumDomainValue: number,
  startOfDayOffset: number,
  hours: number
): readonly [number, number] => [
  startOfDayOffset,
  startOfDayOffset + oneHour(maximumDomainValue) * hours,
];

export const defaultToFullDay = (maximumDomainValue: number): boolean =>
  maximumDomainValue > 2096;

// Note that the data selected for the timeline covers two full days with half day on either side of the day
// being analyzed; consequently, the domain is being covers 48 hours.
export const oneHour = (maximumDomainValue: number): number =>
  maximumDomainValue / 48;

const contractLeft = (
  selection: SelectorBrushSelection,
  brushAreaWidth: number
): SelectorBrushSelection => [
  Math.min(selection[1], selection[0] + oneHour(brushAreaWidth)),
  selection[1],
];

const contractRight = (
  selection: SelectorBrushSelection,
  brushAreaWidth: number
): SelectorBrushSelection => [
  selection[0],
  Math.max(selection[0], selection[1] - oneHour(brushAreaWidth)),
];

const expandLeft = (
  selection: SelectorBrushSelection,
  brushAreaWidth: number
): SelectorBrushSelection => [
  Math.max(0, selection[0] - oneHour(brushAreaWidth)),
  selection[1],
];

const expandRight = (
  selection: SelectorBrushSelection,
  brushAreaWidth: number
): SelectorBrushSelection => [
  selection[0],
  Math.min(brushAreaWidth, selection[1] + oneHour(brushAreaWidth)),
];

const brushSelections = (
  bg: D3Selection.Selection<SVGGElement, unknown, HTMLElement, Timeline>
): SelectorBrushSelection | undefined => {
  if (bg) {
    const node = bg.node() ?? undefined;
    if (node) {
      return D3Brush.brushSelection(node) as SelectorBrushSelection;
    }
  }
  return undefined;
};

export const moveBrush = (
  bg: D3Selection.Selection<SVGGElement, unknown, HTMLElement, Timeline>,
  brush: D3Brush.BrushBehavior<Timeline>,
  selection: SelectorBrushSelection
) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/unbound-method
  bg.call(brush.move, selection);
};

type GenerateProps = ZoneProp &
  Scrolling & {
    readonly brushAreaWidth: number;
    readonly bg: D3Selection.Selection<
      SVGGElement,
      unknown,
      HTMLElement,
      Timeline
    >;
    readonly brush: D3Brush.BrushBehavior<Timeline>;
    readonly milestones: readonly Timeline.Milestone.Timezone[];
  };

export type BrushHandlers = Timeline.Scroll.Events & {
  readonly onSetBrush: (selection: SelectorBrushSelection) => void;
};

export const generate = ({
  brushAreaWidth,
  bg,
  brush,
  milestones,
  defaultZone,
}: GenerateProps): BrushHandlers => {
  const minTime = oneHour(brushAreaWidth);

  return {
    // onGetBrush: () => brushSelections(bg),
    onSetBrush: (selection) => moveBrush(bg, brush, selection),
    onLeft: () => {
      const brushSelection = brushSelections(bg);
      if (brushSelection && brushSelection[0] > 0) {
        const moveDelta: number = Math.min(
          Math.max(brushSelection[0] - minTime, 0),
          minTime
        );
        moveBrush(bg, brush, [
          Math.max(brushSelection[0] - moveDelta, 0),
          Math.max(brushSelection[1] - moveDelta, moveDelta),
        ]);
      }
    },
    onLeftMax: () => {
      const brushSelection = brushSelections(bg);
      if (brushSelection && brushSelection[0] > 0) {
        moveBrush(bg, brush, [0, brushSelection[1] - brushSelection[0]]);
      }
    },
    onRight: () => {
      const brushSelection = brushSelections(bg);
      if (brushSelection && brushSelection[1] < brushAreaWidth) {
        const moveDelta: number = Math.min(
          Math.max(brushAreaWidth - brushSelection[1], 0),
          minTime
        );
        moveBrush(bg, brush, [
          Math.min(brushSelection[0] + moveDelta, brushAreaWidth - 1),
          Math.min(brushSelection[1] + moveDelta, brushAreaWidth),
        ]);
      }
    },
    onRightMax: () => {
      const brushSelection = brushSelections(bg);
      if (brushSelection) {
        moveBrush(bg, brush, [
          brushAreaWidth - (brushSelection[1] - brushSelection[0]),
          brushAreaWidth,
        ]);
      }
    },
    onZoomIn: () => {
      const brushSelection = brushSelections(bg);
      if (brushSelection) {
        const newSelection = contractRight(
          contractLeft(brushSelection, brushAreaWidth),
          brushAreaWidth
        );
        if (
          newSelection[0] !== brushSelection[0] ||
          newSelection[1] !== brushSelection[1]
        ) {
          moveBrush(bg, brush, newSelection);
        }
      }
    },
    onZoomOut: () => {
      const brushSelection = brushSelections(bg);
      if (brushSelection) {
        const newSelection = expandRight(
          expandLeft(brushSelection, brushAreaWidth),
          brushAreaWidth
        );
        moveBrush(bg, brush, newSelection);
        if (
          newSelection[0] !== brushSelection[0] ||
          newSelection[1] !== brushSelection[1]
        ) {
          moveBrush(bg, brush, newSelection);
        }
      }
    },
    onZoomReset: () => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (bg && brush) {
        moveBrush(
          bg,
          brush,
          defaultToFullDay(brushAreaWidth)
            ? defaultSelection(
                brushAreaWidth,
                Brush.brushStartOfDayForZone(
                  brushAreaWidth,
                  milestones,
                  defaultZone
                ),
                24
              )
            : defaultSelection(
                brushAreaWidth,
                Brush.brushStartOfDayForZone(
                  brushAreaWidth,
                  milestones,
                  defaultZone
                ),
                12
              )
        );
      }
    },
  };
};
